/**
 * A family member's character, drawn as SVG. No image files, no network.
 *
 * Ported from apps/display/public/avatar.js -- the procedural renderer, which
 * is complete and polished -- and held to it by avatar.parity.test.ts, which
 * renders the same specs through both and compares the SVG byte for byte.
 *
 * WHY THIS ONE AND NOT THE DISPLAY'S V12. The wall display is part-way to a
 * new renderer (V12) that draws people from painted "activity" prototypes:
 * ~285 MB of PNG, colour-split into skin, clothes and hair at load, most of it
 * still marked as candidates, and by its own status note never yet seen in a
 * browser. Porting that now would be porting work in progress. What IS stable
 * is the DATA -- the spec below, and V12's seven choices -- so the web app
 * stores exactly that shape. When V12's art is accepted, it can draw the same
 * saved characters without anybody rebuilding theirs.
 *
 * THE SPEC HAS MORE FIELDS THAN THE BUILDER OFFERS. V12 removed face shape,
 * eyes, brows and mouth from the builder because the paintings fix them; the
 * builder here offers V12's seven so a look made in either place means the
 * same thing in both. The other fields keep their defaults and this renderer
 * draws them, which is why every character has a friendly round face and a
 * smile rather than a blank.
 *
 * Nothing here is a photograph and nothing is free text: a character is a
 * handful of small integers, which is what makes it safe to store and cheap
 * to encrypt alongside a child's name.
 */

const SKIN = ['#F6DCC4', '#EFC7A5', '#E3AE85', '#CE8F64', '#B27248', '#8E5732', '#6B4026', '#4B2C1B'];
const HAIR = ['#2B1F1A', '#4A3226', '#7B4B2A', '#A9682F', '#C98F3F', '#E6C98A', '#9AA0A6', '#DCD6C8', '#7A3C63', '#39607A'];
const SHIRT = ['#3E7A46', '#2F6B8A', '#8A4A55', '#C2803A', '#6B5B95', '#B5495B', '#38706B', '#4A4E69', '#C9A23F', '#5E7040', '#EEE3CA'];
const HEADS = ['round', 'oval', 'square', 'heart'];
const HAIRS = ['short', 'swoop', 'curly', 'long', 'ponytail', 'buzz', 'bun', 'braids', 'long-curly', 'full-curls'];
const EYE_COLORS = ['#755037', '#397793', '#54734A', '#9A813F', '#6D7682', '#393029'];
const EYES = ['round', 'happy', 'wide', 'sleepy', 'star', 'wink'];
const BROWS = ['soft', 'straight', 'raised', 'angled'];
const MOUTHS = ['smile', 'grin', 'small', 'open', 'tongue', 'calm'];
const EXTRAS = ['none', 'glasses', 'rounds', 'freckles'];
const SHIRTS = ['tee', 'stripes', 'hoodie', 'collar', 'overalls', 'dress'];
/** V12's four painted outfits, which replace the six cutout garments. */
const OUTFITS = ['Tee and shorts', 'Tee and jeans', 'Long dress', 'Shirt and skirt'];

export interface AvatarSpec {
  skin: number;
  head: number;
  hair: number;
  hairColor: number;
  brows: number;
  eyes: number;
  mouth: number;
  extra: number;
  shirt: number;
  shirtColor: number;
  build: number;
  height: number;
  eyeColor: number;
  outfit: number;
}

interface Group {
  label: string;
  swatches?: readonly string[];
  options?: readonly string[];
}

export const catalogue: Record<string, Group> = {
  skin: { label: 'Skin', swatches: SKIN },
  hair: { label: 'Hair', options: ['Bob', 'Tousled', 'Curls', 'Long', 'Ponytail', 'Cropped', 'Bun', 'Braids', 'Long curly', 'Full curls'] },
  hairColor: { label: 'Hair colour', swatches: HAIR },
  head: { label: 'Face shape', options: HEADS },
  eyeColor: { label: 'Eye colour', swatches: EYE_COLORS },
  brows: { label: 'Eyebrows', options: BROWS },
  eyes: { label: 'Eyes', options: EYES },
  mouth: { label: 'Mouth', options: MOUTHS },
  extra: { label: 'Extras', options: EXTRAS },
  shirt: { label: 'Clothes', options: SHIRTS },
  shirtColor: { label: 'Clothes colour', swatches: SHIRT },
  outfit: { label: 'Clothes', options: OUTFITS },
  build: { label: 'Build', options: ['Slender', 'Medium', 'Broad'] },
  height: { label: 'Height', options: ['Short', 'Medium', 'Tall'] },
};

export const counts = {
  skin: SKIN.length, head: HEADS.length, hair: HAIRS.length, hairColor: HAIR.length,
  brows: BROWS.length, eyes: EYES.length, mouth: MOUTHS.length, extra: EXTRAS.length,
  shirt: SHIRTS.length, shirtColor: SHIRT.length, build: 3, height: 3, eyeColor: EYE_COLORS.length,
  outfit: OUTFITS.length,
} as const;

const pick = <T,>(list: readonly T[], n: number): T => list[(((n | 0) % list.length) + list.length) % list.length]!;
const channels = (hex: string): number[] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const hexOf = (rgb: number[]): string =>
  '#' + rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');
const blend = (hex: string, rgb: number[], amount: number): string =>
  hexOf(channels(hex).map((c, i) => c + (rgb[i]! - c) * amount));

const VALLEY_INK = [42, 32, 21];
// Every outline in the drawing: related to the skin, but pulled into the
// paintings' ink range so a family reads as one set of drawings.
const ink = (hex: string) => blend(hex, VALLEY_INK, 0.62);
const dark = (hex: string) => blend(hex, [0, 0, 0], 0.28);

/**
 * Any stored value, made into a valid spec. Old characters, partial ones and
 * nonsense all come out drawable -- a character must never fail to render
 * because of what somebody saved years ago.
 */
export function normalise(spec?: Partial<Record<keyof AvatarSpec, unknown>> | null): AvatarSpec {
  const s = (spec || {}) as Record<string, unknown>;
  const num = (v: unknown, d: number) => (Number.isFinite(+(v as number)) ? +(v as number) : d);
  return {
    skin: num(s.skin, 1), head: num(s.head, 0), hair: num(s.hair, 0), hairColor: num(s.hairColor, 1),
    brows: num(s.brows, 0), eyes: num(s.eyes, 0), mouth: num(s.mouth, 0),
    extra: num(s.extra, 0) < 4 ? num(s.extra, 0) : 0,
    shirt: num(s.shirt, 0), shirtColor: num(s.shirtColor, 0), build: 1, height: 1,
    eyeColor: Math.max(0, Math.min(EYE_COLORS.length - 1, Math.trunc(num(s.eyeColor, 0)))),
    outfit: Math.max(0, Math.min(OUTFITS.length - 1,
      Math.trunc(num(s.outfit, [0, 0, 0, 1, 1, 2][Math.max(0, Math.min(5, Math.trunc(num(s.shirt, 0))))]!)))),
  };
}

function headGeometry(kind: string): string {
  if (kind === 'oval') return '<ellipse cx="100" cy="84" rx="50" ry="63"/>';
  if (kind === 'square') return '<rect x="46" y="26" width="108" height="116" rx="30"/>';
  if (kind === 'heart') return '<path d="M100 149c-30-18-54-40-54-70 0-30 24-47 54-47s54 17 54 47c0 30-24 52-54 70z"/>';
  return '<ellipse cx="100" cy="84" rx="56" ry="58"/>';
}

function headShape(kind: string, skin: string, line: string): string {
  return headGeometry(kind).replace('/>', ` fill="${skin}" stroke="${line}" stroke-width="2.6"/>`);
}

function modelling(kind: string, uid: number, skin: string): string {
  const light = blend(skin, [255, 248, 224], 0.42);
  const shade = blend(skin, [64, 58, 44], 0.30);
  return `<defs><clipPath id="hc${uid}">${headGeometry(kind)}</clipPath>` +
    `<radialGradient id="hl${uid}" cx=".34" cy=".26" r=".72">` +
      `<stop stop-color="${light}" stop-opacity=".85"/><stop offset="1" stop-color="${light}" stop-opacity="0"/></radialGradient>` +
    `<radialGradient id="hs${uid}" cx=".74" cy=".92" r=".66">` +
      `<stop stop-color="${shade}" stop-opacity=".5"/><stop offset="1" stop-color="${shade}" stop-opacity="0"/></radialGradient>` +
    `</defs><g clip-path="url(#hc${uid})">` +
      `<rect x="28" y="-8" width="150" height="172" fill="url(#hl${uid})"/>` +
      `<rect x="28" y="-8" width="150" height="172" fill="url(#hs${uid})"/></g>`;
}

function hairShape(kind: string, c: string, line: string): string {
  const edge = `stroke="${line}" stroke-width="2.2" stroke-linejoin="round"`;
  if (kind === 'buzz') return `<path d="M46 76c0-32 24-50 54-50s54 18 54 50c-10-18-28-26-54-26S56 58 46 76z" fill="${c}" ${edge}/>`;
  if (kind === 'swoop') return `<path d="M44 78c-2-36 24-54 56-54 26 0 46 12 52 32-16-8-40-2-58 10-14 9-22 20-24 34-10-6-18-12-26-22z" fill="${c}" ${edge}/>`;
  if (kind === 'curly') return `<path d="M42 80c-8-14 0-26 8-30-4-16 10-26 22-24 6-12 26-16 38-6 14-6 30 4 30 18 14 2 20 18 12 30 6 12-2 22-10 24 2-24-14-40-46-40S40 62 42 80z" fill="${c}" ${edge}/>`;
  if (kind === 'long') return `<path d="M42 78c0-34 26-52 58-52s58 18 58 52v74c-10 6-18 2-20-10-2-30-6-46-12-54-14 10-58 10-72 0-6 8-10 24-12 54-2 12-10 16-20 10z" fill="${c}" ${edge}/>`;
  if (kind === 'ponytail') return `<path d="M44 78c0-32 24-52 56-52s56 20 56 52c-12-18-30-26-56-26S56 60 44 78z" fill="${c}" ${edge}/><path d="M150 60c20 4 30 24 26 48-3 20-14 32-24 34 10-16 12-34 4-50-4-9-8-16-6-32z" fill="${c}" ${edge}/>`;
  if (kind === 'bun') return `<circle cx="100" cy="16" r="20" fill="${c}" ${edge}/><path d="M46 78c0-32 24-50 54-50s54 18 54 50c-12-18-28-26-54-26S58 60 46 78z" fill="${c}" ${edge}/>`;
  if (kind === 'braids') return `<path d="M44 78c0-32 24-52 56-52s56 20 56 52c-12-18-30-26-56-26S56 60 44 78z" fill="${c}" ${edge}/><path d="M46 84c-12 8-16 30-10 48 4 12 16 14 20 4 4-12 0-34-10-52z" fill="${c}" ${edge}/><path d="M154 84c12 8 16 30 10 48-4 12-16 14-20 4-4-12 0-34 10-52z" fill="${c}" ${edge}/>`;
  return `<path d="M44 80c0-34 26-54 56-54s56 20 56 54c-14-22-32-30-56-30S58 58 44 80z" fill="${c}" ${edge}/>`;
}

function hairContact(uid: number, skin: string): string {
  const shade = blend(skin, [58, 46, 32], 0.34);
  return `<g clip-path="url(#hc${uid})"><path d="M40 44q60 34 120 0v22q-60 26-120 0z" fill="${shade}" opacity=".28"/></g>`;
}

function eyeShape(kind: string, line: string): string {
  const L = 78, R = 122, y = 88;
  if (kind === 'happy') return `<path d="M${L - 11} ${y + 3}q11-13 22 0M${R - 11} ${y + 3}q11-13 22 0" fill="none" stroke="${line}" stroke-width="4.5" stroke-linecap="round"/>`;
  if (kind === 'wide') return `<circle cx="${L}" cy="${y}" r="11" fill="#fff" stroke="${line}" stroke-width="2.5"/><circle cx="${R}" cy="${y}" r="11" fill="#fff" stroke="${line}" stroke-width="2.5"/><circle cx="${L + 1}" cy="${y + 1}" r="5.5" fill="${line}"/><circle cx="${R + 1}" cy="${y + 1}" r="5.5" fill="${line}"/>`;
  if (kind === 'sleepy') return `<path d="M${L - 10} ${y}h20M${R - 10} ${y}h20" stroke="${line}" stroke-width="4.5" stroke-linecap="round"/>`;
  if (kind === 'star') return `<path d="M${L} ${y - 11}l3.2 7 7.8.8-5.8 5.3 1.7 7.6L${L} ${y + 3}l-6.9 3.7 1.7-7.6-5.8-5.3 7.8-.8z" fill="${line}"/><path d="M${R} ${y - 11}l3.2 7 7.8.8-5.8 5.3 1.7 7.6L${R} ${y + 3}l-6.9 3.7 1.7-7.6-5.8-5.3 7.8-.8z" fill="${line}"/>`;
  if (kind === 'wink') return `<circle cx="${L}" cy="${y}" r="7.5" fill="${line}"/><path d="M${R - 11} ${y + 2}q11-12 22 0" fill="none" stroke="${line}" stroke-width="4.5" stroke-linecap="round"/>`;
  return `<circle cx="${L}" cy="${y}" r="8" fill="${line}"/><circle cx="${R}" cy="${y}" r="8" fill="${line}"/><circle cx="${L + 3}" cy="${y - 3}" r="2.6" fill="#fff"/><circle cx="${R + 3}" cy="${y - 3}" r="2.6" fill="#fff"/>`;
}

function browShape(kind: string, c: string): string {
  const L = 78, R = 122, y = 68, w = 'stroke-width="4.5" stroke-linecap="round" fill="none"';
  if (kind === 'straight') return `<path d="M${L - 13} ${y}h26M${R - 13} ${y}h26" stroke="${c}" ${w}/>`;
  if (kind === 'raised') return `<path d="M${L - 13} ${y + 2}q13-9 26-6M${R - 13} ${y - 4}q13-3 26 6" stroke="${c}" ${w}/>`;
  if (kind === 'angled') return `<path d="M${L - 13} ${y - 4}l26 7M${R - 13} ${y + 3}l26-7" stroke="${c}" ${w}/>`;
  return `<path d="M${L - 13} ${y + 2}q13-10 26 0M${R - 13} ${y + 2}q13-10 26 0" stroke="${c}" ${w}/>`;
}

function mouthShape(kind: string, line: string): string {
  const y = 116;
  if (kind === 'grin') return `<path d="M78 ${y - 3}q22 24 44 0z" fill="#fff" stroke="${line}" stroke-width="3" stroke-linejoin="round"/>`;
  if (kind === 'small') return `<path d="M92 ${y}q8 7 16 0" fill="none" stroke="${line}" stroke-width="4" stroke-linecap="round"/>`;
  if (kind === 'open') return `<ellipse cx="100" cy="${y + 2}" rx="13" ry="11" fill="#7C3B3B" stroke="${line}" stroke-width="3"/>`;
  if (kind === 'tongue') return `<path d="M80 ${y - 2}q20 22 40 0z" fill="#7C3B3B" stroke="${line}" stroke-width="3" stroke-linejoin="round"/><path d="M92 ${y + 9}q8 10 16 0z" fill="#E8798A"/>`;
  if (kind === 'calm') return `<path d="M88 ${y}h24" stroke="${line}" stroke-width="4" stroke-linecap="round"/>`;
  return `<path d="M82 ${y - 2}q18 18 36 0" fill="none" stroke="${line}" stroke-width="4.5" stroke-linecap="round"/>`;
}

function extraShape(kind: string, line: string, hairC: string): string {
  if (kind === 'glasses') return `<g fill="none" stroke="${line}" stroke-width="3.5"><rect x="63" y="76" width="30" height="24" rx="6"/><rect x="107" y="76" width="30" height="24" rx="6"/><path d="M93 87h14M63 85l-14-4M137 85l14-4"/></g>`;
  if (kind === 'rounds') return `<g fill="none" stroke="${line}" stroke-width="3.5"><circle cx="78" cy="88" r="15"/><circle cx="122" cy="88" r="15"/><path d="M93 88h14M63 85l-14-4M137 85l14-4"/></g>`;
  if (kind === 'freckles') return `<g fill="${line}" opacity=".42"><circle cx="70" cy="104" r="2.4"/><circle cx="79" cy="109" r="2.2"/><circle cx="62" cy="111" r="2.1"/><circle cx="130" cy="104" r="2.4"/><circle cx="121" cy="109" r="2.2"/><circle cx="138" cy="111" r="2.1"/></g>`;
  if (kind === 'headband') return `<path d="M47 54q53-22 106 0" fill="none" stroke="${hairC}" stroke-width="10" stroke-linecap="round"/><circle cx="145" cy="50" r="9" fill="${hairC}" stroke="${line}" stroke-width="2.5"/>`;
  if (kind === 'cap') return `<path d="M45 50c0-28 23-44 55-44s55 16 55 44z" fill="${hairC}" stroke="${line}" stroke-width="2.6" stroke-linejoin="round"/><path d="M45 50h122c6 0 8 9 0 11H51c-6 0-8-11-6-11z" fill="${dark(hairC)}" stroke="${line}" stroke-width="2.6" stroke-linejoin="round"/>`;
  return '';
}

function shirtShape(kind: string, c: string, line: string): string {
  const d = dark(c), edge = `stroke="${line}" stroke-width="2.8" stroke-linejoin="round"`;
  const base = `<path d="M100 150c-18 0-34 6-44 14-14 11-18 28-18 50v86h124v-86c0-22-4-39-18-50-10-8-26-14-44-14z" fill="${c}" ${edge}/>`;
  const arms = `<path d="M56 164c-12 6-18 18-18 34v30h16v-64zM144 164c12 6 18 18 18 34v30h-16v-64z" fill="${d}" ${edge}/>`;
  if (kind === 'stripes') return base + `<g fill="${d}"><rect x="40" y="192" width="120" height="12"/><rect x="40" y="220" width="120" height="12"/><rect x="40" y="248" width="120" height="12"/></g>` + arms;
  if (kind === 'hoodie') return base + `<path d="M74 152q26 26 52 0 12 4 18 10-20 22-44 22t-44-22q6-6 18-10z" fill="${d}" ${edge}/><path d="M100 186v26" stroke="${line}" stroke-width="3"/>` + arms;
  if (kind === 'collar') return base + `<path d="M84 152l16 22 16-22 8 4-24 32-24-32z" fill="#FBF6E6" ${edge}/>` + arms;
  if (kind === 'overalls') return `<path d="M100 150c-18 0-34 6-44 14-14 11-18 28-18 50v86h124v-86c0-22-4-39-18-50-10-8-26-14-44-14z" fill="#FBF6E6" ${edge}/>` + `<path d="M66 176h68v124H66z" fill="${c}" ${edge}/><path d="M72 156l-6 22M128 156l6 22" stroke="${c}" stroke-width="11" stroke-linecap="round"/>` + arms;
  if (kind === 'dress') return `<path d="M100 150c-16 0-30 6-38 14l-26 136h128L138 164c-8-8-22-14-38-14z" fill="${c}" ${edge}/>` + arms;
  return base + arms;
}

let seq = 0;

export interface RenderOptions {
  mode?: 'head' | 'full';
  size?: number;
  title?: string;
}

/**
 * An SVG string. Ids inside it are made unique per call, so any number of
 * characters can share a page without one's clip path cutting another.
 */
export function render(spec: Partial<AvatarSpec> | null | undefined, opts?: RenderOptions): string {
  const s = normalise(spec), o = opts || {};
  const mode = o.mode === 'full' ? 'full' : 'head';
  const size = o.size || (mode === 'full' ? 150 : 44);
  const uid = (seq = (seq + 1) % 100000);
  const skin = pick(SKIN, s.skin), line = ink(skin);
  const hairC = pick(HAIR, s.hairColor), shirtC = pick(SHIRT, s.shirtColor);
  const head = pick(HEADS, s.head), hair = pick(HAIRS, s.hair), extra = pick(EXTRAS, s.extra);
  const body = mode === 'full'
    ? shirtShape(pick(SHIRTS, s.shirt), shirtC, line) +
      `<path d="M86 132h28v26h-28z" fill="${blend(skin, VALLEY_INK, 0.14)}" stroke="${line}" stroke-width="2.8"/>`
    : '';
  const ears = `<ellipse cx="46" cy="92" rx="9" ry="12" fill="${blend(skin, VALLEY_INK, 0.1)}" stroke="${line}" stroke-width="2.6"/>` +
               `<ellipse cx="154" cy="92" rx="9" ry="12" fill="${blend(skin, VALLEY_INK, 0.1)}" stroke="${line}" stroke-width="2.6"/>`;
  const face = ears + headShape(head, skin, line) + modelling(head, uid, skin) +
               hairShape(hair, hairC, line) + hairContact(uid, skin) +
               browShape(pick(BROWS, s.brows), ink(hairC)) + eyeShape(pick(EYES, s.eyes), pick(EYE_COLORS, s.eyeColor)) +
               mouthShape(pick(MOUTHS, s.mouth), line) + extraShape(extra, line, hairC);
  const box = mode === 'full' ? '0 -6 200 306' : '28 -4 144 160';
  const ratio = mode === 'full' ? 306 / 200 : 160 / 144;
  const title = o.title ? `<title>${String(o.title).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!)}</title>` : '';
  return `<svg class="avatar avatar-${mode}" viewBox="${box}" width="${size}" height="${Math.round(size * ratio)}" ` +
         `role="img" aria-hidden="${o.title ? 'false' : 'true'}" focusable="false">${title}` +
         `${mode === 'full' ? body : ''}${face}</svg>`;
}

/** A random character. Seeded, so a test can ask for the same one twice. */
export function random(seed?: number): AvatarSpec {
  let n = (seed == null ? Date.now() : seed) >>> 0;
  // The full random word: low LCG bits correlate hair, skin and clothing.
  const next = (max: number) => {
    n = (Math.imul(n, 1664525) + 1013904223) >>> 0;
    return Math.floor((n / 4294967296) * max);
  };
  return {
    skin: next(SKIN.length), head: next(HEADS.length), hair: next(HAIRS.length),
    hairColor: next(HAIR.length), brows: next(BROWS.length), eyes: next(EYES.length),
    mouth: next(MOUTHS.length), extra: next(EXTRAS.length), shirt: next(SHIRTS.length), outfit: next(OUTFITS.length),
    shirtColor: next(SHIRT.length), build: 1, height: 1, eyeColor: next(EYE_COLORS.length),
  };
}

/**
 * The legacy garment that best draws one of V12's outfits in this renderer's
 * torso, so choosing "Long dress" shows a dress. The inverse of the display's
 * `outfitOf` mapping, which reads `shirt` forward into `outfit`.
 */
export function shirtFor(outfit: number): number {
  return [0, 0, 5, 3][outfit] ?? 0;
}

/**
 * A plain glyph per outfit, for the chooser. Ported from `outfitIcon` in the
 * display's avatar-idle.js: cheaper and clearer in a row of buttons than
 * drawing four whole characters just to show four sets of clothes.
 */
export function outfitIcon(outfit: number, color: string, size = 56): string {
  const body = ({
    0: '<path d="M14 10h20l6 4-4 6-2-2v12H14V18l-2 2-4-6z"/><path d="M15 32h18v10h-7v-8h-4v8h-7z"/>',
    1: '<path d="M14 10h20l6 4-4 6-2-2v12H14V18l-2 2-4-6z"/><path d="M15 32h18v22h-8V38h-2v16h-8z"/>',
    2: '<path d="M16 10h16l5 4-3 5-1-1v4l7 31H13l7-31v-4l-1 1-3-5z"/>',
    3: '<path d="M14 10h20l5 4-3 5-2-2v11H14V17l-2 2-4-5z"/><path d="M12 30h24l4 16H8z"/>',
  } as Record<number, string>)[outfit] ?? '';
  return `<svg viewBox="0 0 48 58" width="${size}" height="${(size * 58 / 48).toFixed(0)}" aria-hidden="true" focusable="false"><g fill="${color}" stroke="#4a4336" stroke-width="1.6" stroke-linejoin="round">${body}</g></svg>`;
}
